/**
 * 上传状态全局管理：支持多会话并发上传，后台执行
 *
 * ==================== 性能优化说明（支持 1 万张图片上传） ====================
 *
 * 1. 状态规范化（Normalized State）
 *    - 将 `files: UploadFileItem[]` 改为 `filesById: Record<string, UploadFileItem>` + `fileIds: string[]`
 *    - 优点：更新单个文件状态时只需 `filesById[id] = {...}` O(1)，而非重建整个数组 O(n)
 *    - 10,000 个文件 × 10,000 次更新 = O(n) vs 原来的 O(n²)
 *
 * 2. Blob URL 延迟创建与及时释放
 *    - 不在 addFilesToSession 时为每个文件创建 blob URL
 *    - 通过 getPreviewUrl(fileId) 方法延迟创建，仅在需要显示预览时创建
 *    - 上传成功后立即释放 blob URL，避免内存泄漏
 *    - 浏览器对 blob URL 有数量限制（约 10,000 个），延迟创建可避免触及限制
 *
 * 3. 细粒度订阅支持
 *    - 提供 `getSessionProgress`、`getSessionStatus` 等选择器
 *    - 组件可按需订阅，避免无关状态变化触发重渲染
 *
 * 4. 批量状态更新
 *    - 使用 Immer-like 的局部更新模式，仅更新变化的部分
 *    - 避免频繁的全量状态重建
 *
 * ==========================================================================
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  uploadService,
  UPLOAD_CONFIG,
  createUploadSessionId,
  getBatchUploadUrls,
  sanitizeUploadFilename,
} from '@/services/uploadService'
import type { UploadUrlResponse } from '@/services/uploadService'

// ==================== 类型定义 ====================

/**
 * 上传会话状态
 */
type SessionStatus = 'idle' | 'uploading' | 'paused' | 'completed' | 'error'

/**
 * 上传文件项（不含 previewUrl，延迟创建）
 */
export interface UploadFileItem {
  id: string
  file: File
  uploadFilename: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  url?: string | undefined
  error?: string | undefined
}

/**
 * 带预览 URL 的文件项（用于组件显示）
 */
export interface UploadFileItemWithPreview extends UploadFileItem {
  previewUrl: string
}

/**
 * 上传进度
 */
export interface UploadProgress {
  total: number
  uploaded: number
  failed: number
  percent: number
  currentFile: string
}

/**
 * 单个上传会话（规范化结构）
 */
export interface UploadSession {
  id: string
  fieldName: string
  /** 文件 ID 列表（保持顺序） */
  fileIds: string[]
  /** 文件 ID -> 文件项映射（快速查找） */
  filesById: Record<string, UploadFileItem>
  progress: UploadProgress
  status: SessionStatus
  uploadedUrls: string[]
  startTime: number
  error: string | null
  abortController: AbortController | null
}

/**
 * 兼容旧 API 的文件列表访问（带 previewUrl）
 */
export interface UploadSessionWithFiles extends Omit<UploadSession, 'fileIds' | 'filesById'> {
  files: UploadFileItemWithPreview[]
}

/**
 * 上传状态接口
 */
interface UploadState {
  sessions: Record<string, UploadSession>
  /** 已创建的 blob URL 缓存（用于延迟创建和释放） */
  previewUrlCache: Record<string, string>
}

/**
 * 上传操作接口
 */
interface UploadActions {
  createSession: (fieldName: string) => string
  addFilesToSession: (sessionId: string, newFiles: File[]) => void
  updateFileStatus: (
    sessionId: string,
    fileId: string,
    status: UploadFileItem['status'],
    url?: string,
    error?: string
  ) => void
  updateProgress: (sessionId: string, progress: Partial<UploadProgress>) => void
  setSessionStatus: (sessionId: string, status: SessionStatus, error?: string) => void
  removeFile: (sessionId: string, fileId: string) => void
  clearSession: (sessionId: string) => void
  startUpload: (
    sessionId: string,
    onComplete?: (urls: string[]) => void
  ) => Promise<void>
  cancelUpload: (sessionId: string) => void
  getSession: (sessionId: string) => UploadSession | undefined
  /** 获取带 files 数组的会话（兼容旧 API） */
  getSessionWithFiles: (sessionId: string) => UploadSessionWithFiles | undefined
  hasActiveUploads: () => boolean
  getUploadedUrls: (sessionId: string) => string[]
  /** 获取或创建预览 URL（延迟创建） */
  getPreviewUrl: (sessionId: string, fileId: string) => string
  /** 释放预览 URL */
  revokePreviewUrl: (fileId: string) => void
  // 细粒度选择器
  getSessionProgress: (sessionId: string) => UploadProgress | undefined
  getSessionStatus: (sessionId: string) => SessionStatus | undefined
  getFileCount: (sessionId: string) => number
}

type UploadStore = UploadState & UploadActions

// ==================== 常量 ====================

const STORE_NAME = 'UploadStore' as const

function splitUploadFilename(filename: string): { name: string; ext: string } {
  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex <= 0) {
    return {
      name: filename === '' ? 'file' : filename,
      ext: '',
    }
  }

  return {
    name: filename.slice(0, dotIndex) || 'file',
    ext: filename.slice(dotIndex),
  }
}

function buildUniqueUploadFilename(filename: string, usedFilenames: Set<string>): string {
  const sanitizedFilename = sanitizeUploadFilename(filename)
  const { name, ext } = splitUploadFilename(sanitizedFilename)

  let candidate = sanitizedFilename === '' ? `file${ext}` : sanitizedFilename
  let sequence = 1

  while (usedFilenames.has(candidate)) {
    candidate = `${name}_${String(sequence)}${ext}`
    sequence++
  }

  usedFilenames.add(candidate)
  return candidate
}

// ==================== Store 实现 ====================

export const useUploadStore = create<UploadStore>()(
  devtools(
    (set, get): UploadStore => ({
      sessions: {},
      previewUrlCache: {},

      /**
       * 创建新的上传会话
       * @param fieldName 关联的表单字段名
       * @returns 会话 ID
       */
      createSession: (fieldName: string): string => {
        const sessionId = createUploadSessionId()

        const session: UploadSession = {
          id: sessionId,
          fieldName,
          fileIds: [],
          filesById: {},
          progress: {
            total: 0,
            uploaded: 0,
            failed: 0,
            percent: 0,
            currentFile: '',
          },
          status: 'idle',
          uploadedUrls: [],
          startTime: 0,
          error: null,
          abortController: null,
        }

        set(
          (state) => ({
            sessions: {
              ...state.sessions,
              [sessionId]: session,
            },
          }),
          false,
          'createSession'
        )

        return sessionId
      },

      /**
       * 向会话添加文件
       * 优化：不再为每个文件创建 blob URL，延迟到需要显示时创建
       * @param sessionId 会话 ID
       * @param newFiles 新文件列表
       */
      addFilesToSession: (sessionId: string, newFiles: File[]): void => {
        console.log('[UploadStore] addFilesToSession 被调用, sessionId:', sessionId, '文件数:', newFiles.length)
        const session = get().sessions[sessionId]
        console.log('[UploadStore] 当前会话状态:', session)
        if (session === undefined) {
          console.error('[UploadStore] 会话不存在，无法添加文件!')
          return
        }

        const currentCount = session.fileIds.length
        const remainingSlots = UPLOAD_CONFIG.MAX_FILES - currentCount

        if (remainingSlots <= 0) {
          return
        }

        const newFileIds: string[] = []
        const newFilesById: Record<string, UploadFileItem> = {}
        const filesToAdd = newFiles.slice(0, remainingSlots)
        const usedUploadFilenames = new Set(
          Object.values(session.filesById).map((item) => item.uploadFilename)
        )

        filesToAdd.forEach((file, index) => {
          // 验证文件类型和大小
          if (!file.type.startsWith('image/')) {
            return
          }
          if (file.size > UPLOAD_CONFIG.MAX_FILE_SIZE) {
            return
          }

          const fileId = `file-${String(Date.now())}-${String(index)}-${Math.random().toString(36).substring(2, 9)}`
          const uploadFilename = buildUniqueUploadFilename(file.name, usedUploadFilenames)
          newFileIds.push(fileId)
          newFilesById[fileId] = {
            id: fileId,
            file,
            uploadFilename,
            status: 'pending',
          }
        })

        if (newFileIds.length === 0) {
          return
        }

        set(
          (state) => {
            const currentSession = state.sessions[sessionId]
            if (currentSession === undefined) {
              return state
            }

            return {
              sessions: {
                ...state.sessions,
                [sessionId]: {
                  ...currentSession,
                  fileIds: [...currentSession.fileIds, ...newFileIds],
                  filesById: { ...currentSession.filesById, ...newFilesById },
                  progress: {
                    ...currentSession.progress,
                    total: currentSession.fileIds.length + newFileIds.length,
                  },
                },
              },
            }
          },
          false,
          'addFilesToSession'
        )
      },

      /**
       * 更新文件状态
       * 优化：O(1) 更新，只修改 filesById[fileId]
       */
      updateFileStatus: (
        sessionId: string,
        fileId: string,
        status: UploadFileItem['status'],
        url?: string,
        error?: string
      ): void => {
        set(
          (state) => {
            const session = state.sessions[sessionId]
            if (session === undefined) {
              return state
            }

            const existingFile = session.filesById[fileId]
            if (existingFile === undefined) {
              return state
            }

            // 如果上传成功，释放该文件的预览 URL
            if (status === 'success') {
              const previewUrl = state.previewUrlCache[fileId]
              if (previewUrl !== undefined) {
                URL.revokeObjectURL(previewUrl)
              }
            }

            const uploadedUrls =
              status === 'success' && url !== undefined
                ? [...session.uploadedUrls, url]
                : session.uploadedUrls

            return {
              sessions: {
                ...state.sessions,
                [sessionId]: {
                  ...session,
                  filesById: {
                    ...session.filesById,
                    [fileId]: { ...existingFile, status, url, error },
                  },
                  uploadedUrls,
                },
              },
              // 移除已释放的 previewUrl
              previewUrlCache: status === 'success'
                ? Object.fromEntries(
                    Object.entries(state.previewUrlCache).filter(([k]) => k !== fileId)
                  )
                : state.previewUrlCache,
            }
          },
          false,
          'updateFileStatus'
        )
      },

      /**
       * 更新进度
       */
      updateProgress: (sessionId: string, progress: Partial<UploadProgress>): void => {
        set(
          (state) => {
            const session = state.sessions[sessionId]
            if (session === undefined) {
              return state
            }

            return {
              sessions: {
                ...state.sessions,
                [sessionId]: {
                  ...session,
                  progress: {
                    ...session.progress,
                    ...progress,
                  },
                },
              },
            }
          },
          false,
          'updateProgress'
        )
      },

      /**
       * 设置会话状态
       */
      setSessionStatus: (
        sessionId: string,
        status: SessionStatus,
        error?: string
      ): void => {
        set(
          (state) => {
            const session = state.sessions[sessionId]
            if (session === undefined) {
              return state
            }

            return {
              sessions: {
                ...state.sessions,
                [sessionId]: {
                  ...session,
                  status,
                  error: error ?? null,
                },
              },
            }
          },
          false,
          'setSessionStatus'
        )
      },

      /**
       * 移除单个文件
       */
      removeFile: (sessionId: string, fileId: string): void => {
        const state = get()
        const session = state.sessions[sessionId]
        if (session === undefined) {
          return
        }

        // 释放预览 URL
        const previewUrl = state.previewUrlCache[fileId]
        if (previewUrl !== undefined) {
          URL.revokeObjectURL(previewUrl)
        }

        const fileToRemove = session.filesById[fileId]
        const removedUrl = fileToRemove?.url

        set(
          (s) => {
            const currentSession = s.sessions[sessionId]
            if (currentSession === undefined) {
              return s
            }

            // 创建新的 filesById（移除指定文件）
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [fileId]: removed, ...remainingFilesById } = currentSession.filesById

            const updatedUrls = removedUrl !== undefined && removedUrl !== ''
              ? currentSession.uploadedUrls.filter((u) => u !== removedUrl)
              : currentSession.uploadedUrls

            return {
              sessions: {
                ...s.sessions,
                [sessionId]: {
                  ...currentSession,
                  fileIds: currentSession.fileIds.filter((id) => id !== fileId),
                  filesById: remainingFilesById,
                  uploadedUrls: updatedUrls,
                  progress: {
                    ...currentSession.progress,
                    total: currentSession.fileIds.length - 1,
                  },
                },
              },
              previewUrlCache: Object.fromEntries(
                Object.entries(s.previewUrlCache).filter(([k]) => k !== fileId)
              ),
            }
          },
          false,
          'removeFile'
        )
      },

      /**
       * 清空会话
       */
      clearSession: (sessionId: string): void => {
        const state = get()
        const session = state.sessions[sessionId]
        if (session === undefined) {
          return
        }

        // 释放所有预览 URL
        session.fileIds.forEach((fileId) => {
          const previewUrl = state.previewUrlCache[fileId]
          if (previewUrl !== undefined) {
            URL.revokeObjectURL(previewUrl)
          }
        })

        // 取消正在进行的上传
        if (session.abortController !== null) {
          session.abortController.abort()
        }

        set(
          (s) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [sessionId]: removed, ...remainingSessions } = s.sessions
            // 清理该会话相关的预览 URL
            const remainingCache = Object.fromEntries(
              Object.entries(s.previewUrlCache).filter(
                ([fileId]) => !session.fileIds.includes(fileId)
              )
            )
            return {
              sessions: remainingSessions,
              previewUrlCache: remainingCache,
            }
          },
          false,
          'clearSession'
        )
      },

      /**
       * 开始上传
       * @param sessionId 会话 ID
       * @param onComplete 完成回调
       */
      startUpload: async (
        sessionId: string,
        onComplete?: (urls: string[]) => void
      ): Promise<void> => {
        console.log('[UploadStore] startUpload 被调用, sessionId:', sessionId)
        const session = get().sessions[sessionId]
        if (session === undefined || session.status === 'uploading') {
          console.log('[UploadStore] 会话不存在或正在上传中, session:', session?.status)
          return
        }

        const pendingFileIds = session.fileIds.filter(
          (id) => session.filesById[id]?.status === 'pending'
        )
        console.log('[UploadStore] 待上传文件数:', pendingFileIds.length)
        if (pendingFileIds.length === 0) {
          console.log('[UploadStore] 没有待上传的文件')
          return
        }

        const abortController = new AbortController()

        set(
          (state) => {
            const currentSession = state.sessions[sessionId]
            if (currentSession === undefined) {
              return state
            }

            return {
              sessions: {
                ...state.sessions,
                [sessionId]: {
                  ...currentSession,
                  status: 'uploading',
                  startTime: Date.now(),
                  abortController,
                  progress: {
                    ...currentSession.progress,
                    total: pendingFileIds.length,
                    uploaded: 0,
                    failed: 0,
                    percent: 0,
                  },
                },
              },
            }
          },
          false,
          'startUpload'
        )

        const { updateFileStatus, updateProgress, setSessionStatus } = get()

        let uploaded = 0
        let failed = 0

        // 批量获取签名 URL（每批最多 100 个）
        console.log('[UploadStore] 开始批量获取签名 URL...')
        const urlMap = new Map<string, UploadUrlResponse>()

        try {
          const batchSize = UPLOAD_CONFIG.BATCH_URL_LIMIT
          for (let i = 0; i < pendingFileIds.length; i += batchSize) {
            if (abortController.signal.aborted) {
              break
            }

            const batchIds = pendingFileIds.slice(i, i + batchSize)
            const currentSession = get().sessions[sessionId]
            if (currentSession === undefined) {
              break
            }

            const fileInfos = batchIds.map((id) => {
              const fileItem = currentSession.filesById[id]
              return {
                filename: fileItem?.uploadFilename ?? '',
                contentType: fileItem?.file.type ?? '',
              }
            }).filter((info) => info.filename !== '')

            const urls = await getBatchUploadUrls(fileInfos, sessionId)
            console.log(`[UploadStore] 批次 ${String(Math.floor(i / batchSize) + 1)} 获取签名 URL 成功:`, urls.length)

            // 建立文件 ID 到签名 URL 的映射
            batchIds.forEach((id, index) => {
              const urlInfo = urls[index]
              if (urlInfo !== undefined) {
                urlMap.set(id, urlInfo)
              }
            })
          }
        } catch (err) {
          console.error('[UploadStore] 批量获取签名 URL 失败:', err)
          setSessionStatus(sessionId, 'error', '获取上传凭证失败')
          return
        }

        console.log('[UploadStore] 签名 URL 获取完成，开始并发上传...')

        // 并发上传控制
        const queue = [...pendingFileIds]
        const executing = new Set<Promise<void>>()

        while (queue.length > 0 || executing.size > 0) {
          // 检查是否已取消
          if (abortController.signal.aborted) {
            break
          }

          // 填充执行队列
          while (executing.size < UPLOAD_CONFIG.CONCURRENCY && queue.length > 0) {
            const fileId = queue.shift()
            if (fileId === undefined) {
              continue
            }

            const currentSession = get().sessions[sessionId]
            if (currentSession === undefined) {
              continue
            }

            const fileItem = currentSession.filesById[fileId]
            if (fileItem === undefined) {
              continue
            }

            const urlInfo = urlMap.get(fileId)
            if (urlInfo === undefined) {
              console.error('[UploadStore] 未找到签名 URL:', fileItem.file.name)
              updateFileStatus(sessionId, fileId, 'error', undefined, '未找到上传凭证')
              failed++
              continue
            }

            const promise = (async (): Promise<void> => {
              console.log('[UploadStore] 开始上传文件:', fileItem.file.name)
              updateFileStatus(sessionId, fileId, 'uploading')
              updateProgress(sessionId, { currentFile: fileItem.file.name })

              try {
                // 直接使用预获取的签名 URL 上传
                await uploadService.uploadWithSignedUrl(fileItem.file, urlInfo.uploadUrl)
                console.log('[UploadStore] 文件上传成功:', fileItem.file.name, urlInfo.fileUrl)
                updateFileStatus(sessionId, fileId, 'success', urlInfo.fileUrl)
                uploaded++
              } catch (err) {
                console.error('[UploadStore] 文件上传失败:', fileItem.file.name, err)
                const message = err instanceof Error ? err.message : '上传失败'
                updateFileStatus(sessionId, fileId, 'error', undefined, message)
                failed++
              }

              const total = pendingFileIds.length
              const percent = Math.round(((uploaded + failed) / total) * 100)
              updateProgress(sessionId, {
                uploaded,
                failed,
                percent,
                currentFile: '',
              })
            })()

            // 任务完成后自动从 Set 中移除
            const wrappedPromise = promise.finally(() => {
              executing.delete(wrappedPromise)
            })
            executing.add(wrappedPromise)
          }

          // 等待任一任务完成
          if (executing.size > 0) {
            await Promise.race(executing)
          }
        }

        // 等待所有执行中的任务完成
        await Promise.allSettled(executing)

        // 更新最终状态
        const finalSession = get().sessions[sessionId]
        if (finalSession !== undefined) {
          const finalStatus: SessionStatus =
            failed === 0 ? 'completed' : failed === pendingFileIds.length ? 'error' : 'completed'

          setSessionStatus(sessionId, finalStatus)

          // 调用完成回调
          if (onComplete !== undefined) {
            onComplete(finalSession.uploadedUrls)
          }
        }
      },

      /**
       * 取消上传
       */
      cancelUpload: (sessionId: string): void => {
        const session = get().sessions[sessionId]
        if (session === undefined) {
          return
        }

        if (session.abortController !== null) {
          session.abortController.abort()
        }

        set(
          (state) => {
            const currentSession = state.sessions[sessionId]
            if (currentSession === undefined) {
              return state
            }

            return {
              sessions: {
                ...state.sessions,
                [sessionId]: {
                  ...currentSession,
                  status: 'paused',
                  abortController: null,
                },
              },
            }
          },
          false,
          'cancelUpload'
        )
      },

      /**
       * 获取会话（原始规范化结构）
       */
      getSession: (sessionId: string): UploadSession | undefined => {
        return get().sessions[sessionId]
      },

      /**
       * 获取带 files 数组的会话（兼容旧 API）
       * 将规范化结构转换为带 previewUrl 的 files 数组
       */
      getSessionWithFiles: (sessionId: string): UploadSessionWithFiles | undefined => {
        const state = get()
        const session = state.sessions[sessionId]
        if (session === undefined) {
          return undefined
        }

        const files: UploadFileItemWithPreview[] = session.fileIds.map((id) => {
          const file = session.filesById[id]
          if (file === undefined) {
            return null
          }
          // 获取或创建预览 URL
          let previewUrl = state.previewUrlCache[id]
          if (previewUrl === undefined) {
            previewUrl = URL.createObjectURL(file.file)
            // 同步更新缓存（副作用，但必要）
            state.previewUrlCache[id] = previewUrl
          }
          return {
            ...file,
            previewUrl,
          }
        }).filter((f): f is UploadFileItemWithPreview => f !== null)

        return {
          id: session.id,
          fieldName: session.fieldName,
          files,
          progress: session.progress,
          status: session.status,
          uploadedUrls: session.uploadedUrls,
          startTime: session.startTime,
          error: session.error,
          abortController: session.abortController,
        }
      },

      /**
       * 检查是否有活动上传
       */
      hasActiveUploads: (): boolean => {
        const { sessions } = get()
        return Object.values(sessions).some((s) => s.status === 'uploading')
      },

      /**
       * 获取已上传的 URL 列表
       */
      getUploadedUrls: (sessionId: string): string[] => {
        const session = get().sessions[sessionId]
        return session?.uploadedUrls ?? []
      },

      /**
       * 获取或创建预览 URL（延迟创建）
       * 仅在需要显示预览时调用
       */
      getPreviewUrl: (sessionId: string, fileId: string): string => {
        const state = get()
        const cached = state.previewUrlCache[fileId]
        if (cached !== undefined) {
          return cached
        }

        const session = state.sessions[sessionId]
        const file = session?.filesById[fileId]?.file
        if (file === undefined) {
          return ''
        }

        const previewUrl = URL.createObjectURL(file)
        // 更新缓存
        set(
          (s) => ({
            previewUrlCache: {
              ...s.previewUrlCache,
              [fileId]: previewUrl,
            },
          }),
          false,
          'getPreviewUrl'
        )
        return previewUrl
      },

      /**
       * 释放预览 URL
       */
      revokePreviewUrl: (fileId: string): void => {
        const state = get()
        const previewUrl = state.previewUrlCache[fileId]
        if (previewUrl !== undefined) {
          URL.revokeObjectURL(previewUrl)
          set(
            (s) => ({
              previewUrlCache: Object.fromEntries(
                Object.entries(s.previewUrlCache).filter(([k]) => k !== fileId)
              ),
            }),
            false,
            'revokePreviewUrl'
          )
        }
      },

      // ==================== 细粒度选择器 ====================

      /**
       * 获取会话进度（用于细粒度订阅）
       */
      getSessionProgress: (sessionId: string): UploadProgress | undefined => {
        return get().sessions[sessionId]?.progress
      },

      /**
       * 获取会话状态（用于细粒度订阅）
       */
      getSessionStatus: (sessionId: string): SessionStatus | undefined => {
        return get().sessions[sessionId]?.status
      },

      /**
       * 获取文件数量（用于细粒度订阅）
       */
      getFileCount: (sessionId: string): number => {
        return get().sessions[sessionId]?.fileIds.length ?? 0
      },
    }),
    { name: STORE_NAME, enabled: import.meta.env.DEV }
  )
)

export type { UploadState, UploadActions, UploadStore, SessionStatus }
